// ./src/components/features/Questions/ComplianceGapAlerts.tsx
//
// THE TWO WARNINGS A HOST GETS WHEN A WAIVER ON THEIR EVENT CANNOT BE COMPLETED.
//
// ⚠⚠ ONE COMPONENT, TWO SURFACES, ON PURPOSE. The event dashboard shows these for the host who
// never opens the Questions page; the Questions page shows them for the host who came straight there
// to add a question and would otherwise never meet them. Rendering them from one place is not tidying
// — a host who saw a different description of one problem depending on which page they were standing
// on would reasonably conclude they were two problems.
//
// ⚠ The RULE behind them is `utils/complianceSetup.ts`; this file is only how it is said.
//
// ⓘ A THIRD SURFACE, the create wizard's Policies step (#62 P6(e)), passes `inWizard`: every button there stays IN the
//   wizard (a navigate would leave it mid-setup) — the date of birth opens the Questions drawer already filled in, and
//   "Review questions" goes back to the Questions step. "Review compliance" is not offered: that step IS compliance.
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { Alert, Button, Group, Stack, Text } from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { OP_RESULT_CODES } from "@sdflc/api-helpers";
import { STATUSES } from "@sdflc/utils";

import { AppIcon } from "@/components/generic";
import { RsvpQuestionService } from "@/utils/api";
import { ADULT_EMAIL_QUESTION, type ComplianceGap, type ComplianceGaps } from "@/utils/complianceSetup";
import { birthdayQuestionUrl } from "@/pages/private/EventQuestionsPage/questionPrefill";
import { refusalReason } from "@/utils";

/** The create wizard's own way to each fix (see the file header). */
export interface ComplianceGapWizardActions {
  /** Open the Questions drawer with the date-of-birth question filled in. */
  onAddBirthday: () => void;
  /** Go to the wizard's Questions step. */
  onReviewQuestions: () => void;
}

/**
 * Whichever of the two gaps are open, in the order a host should act on them: the date of birth
 * first, because without it the form is REFUSED and no registration completes at all — the email gap
 * only breaks the waiver, silently, after a successful sale.
 */
export const ComplianceGapAlerts: React.FC<{
  eventId: string;
  gaps: ComplianceGaps;
  onFixed: () => void;
  inWizard?: ComplianceGapWizardActions;
}> = ({ eventId, gaps, onFixed, inWizard }) => {
  if (!gaps.birthday && !gaps.adultEmail) {
    return null;
  }

  return (
    <Stack gap="sm">
      {gaps.birthday ? <BirthdayGapAlert gap={gaps.birthday} eventId={eventId} onFixed={onFixed} inWizard={inWizard} /> : null}
      {gaps.adultEmail ? <AdultEmailGapAlert gap={gaps.adultEmail} eventId={eventId} onFixed={onFixed} inWizard={inWizard} /> : null}
    </Stack>
  );
};

/**
 * "Turn it back on" for a SWITCHED-OFF question that would close the gap (owner, 2026-10-01) — better than adding a
 * second copy of a question the host already wrote. The same status change the Questions page makes. Failure is said;
 * success is the alert going away (the caller re-reads its questions in `onFixed`).
 */
const useTurnBackOn = (questionId: string | null, onFixed: () => void) => {
  const { t } = useTranslation(['common']);
  const [turning, setTurning] = useState(false);

  const turnBackOn = async () => {
    if (!questionId) {
      return;
    }

    setTurning(true);

    const result = await RsvpQuestionService.update(questionId, { status: STATUSES.ACTIVE });

    setTurning(false);

    if (result?.code !== OP_RESULT_CODES.OK) {
      notifications.show({
        color: 'red',
        message: refusalReason(result) || t('common:errors.generic', { defaultValue: 'Something went wrong' }),
      });

      return;
    }

    onFixed();
  };

  return { turning, turnBackOn };
};

/** The date-of-birth gap — reading the shared rule. */
const BirthdayGapAlert: React.FC<{ gap: ComplianceGap; eventId: string; onFixed: () => void; inWizard?: ComplianceGapWizardActions }> = ({
  gap: problem,
  eventId,
  onFixed,
  inWizard,
}) => {
  const { t } = useTranslation(['events', 'common']);
  const navigate = useNavigate();
  const { turning, turnBackOn } = useTurnBackOn(problem.turnedOffQuestionId, onFixed);
  const isOff = !!problem.turnedOffQuestionId;

  return (
    <Alert
      color={problem.blocking ? 'red' : 'orange'}
      variant="light"
      icon={<AppIcon iconName="AlertTriangle" size={18} />}
      title={
        problem.blocking
          ? t('events:dash.complianceBlockedTitle', { defaultValue: 'Guests can’t sign your waiver' })
          : t('events:dash.complianceWarnTitle', { defaultValue: 'A waiver can’t be collected' })
      }
    >
      <Stack gap="xs" align="flex-start">
        {/* In the wizard the event is not live yet — nothing is being refused RIGHT NOW, so the page's sentence would be untrue. */}
        <Text size="md">
          {isOff
            ? // The question exists — it is SWITCHED OFF, so guests are never asked it. Saying "no question" would send
              // the host to write a second one.
              problem.blocking
              ? t('events:dash.complianceOffBlockedBody', {
                  defaultValue:
                    'A required agreement on this event needs each guest’s date of birth — to know whether it applies to them, or whether a parent or guardian has to sign for a minor. Your date-of-birth question is turned off, so guests are never asked it, and every RSVP and ticket sale is refused until you turn it back on.',
                })
              : t('events:dash.complianceOffWarnBody', {
                  defaultValue: 'An agreement on this event needs each guest’s date of birth, and your date-of-birth question is turned off, so it is not being collected.',
                })
            : problem.blocking && inWizard
            ? t('events:wizard.policies.blockedBody', {
                defaultValue:
                  'A required agreement on this event needs each guest’s date of birth — to know whether it applies to them, or whether a parent or guardian has to sign for a minor. Your form has no date-of-birth question, so nobody could complete it: every RSVP and ticket sale will be refused until you add one.',
              })
            : problem.blocking
            ? t('events:dash.complianceBlockedBody', {
                defaultValue:
                  'A required agreement on this event needs each guest’s date of birth — to know whether it applies to them, or whether a parent or guardian has to sign for a minor. Your form has no date-of-birth question, so nobody can complete it: RSVPs and ticket sales are being blocked right now.',
              })
            : t('events:dash.complianceWarnBody', {
                defaultValue:
                  'An agreement on this event needs each guest’s date of birth, and your form has no date-of-birth question, so it is not being collected.',
              })}
        </Text>
        {/* Straight to the fix, with the question already filled in — not to the page that would
            tell them again what to do. `birthdayQuestionUrl` derives the value type from the person
            field, which is the part that is easy to get wrong by hand. */}
        <Group gap="xs">
          {isOff ? (
            <Button size="md" loading={turning} onClick={() => void turnBackOn()}>
              {t('events:dash.complianceTurnOn', { defaultValue: 'Turn the date-of-birth question back on' })}
            </Button>
          ) : (
            <Button size="md" onClick={() => (inWizard ? inWizard.onAddBirthday() : navigate(birthdayQuestionUrl(eventId)))}>
              {t('events:dash.complianceAddQuestion', { defaultValue: 'Add date-of-birth question' })}
            </Button>
          )}
          {inWizard ? null : (
            <Button size="md" variant="subtle" onClick={() => navigate(`/events/${eventId}/compliance`)}>
              {t('events:dash.complianceReview', { defaultValue: 'Review compliance' })}
            </Button>
          )}
        </Group>
      </Stack>
    </Alert>
  );
};

/**
 * ⚠⚠ THE ADULT-EMAIL GAP, AND IT IS QUIETER THAN THE ONE ABOVE. A missing date of birth BLOCKS the
 * form — the guest sees an error and complains. A missing adult email blocks nothing: the sale
 * completes, the waiver is minted `PENDING`, and because `persistPolicyAcceptances` addresses it to
 * an email that does not exist, **nothing is ever sent**. The token sits in the database, the adult
 * was never written to, and the buyer cannot open it either. Every party to a legal document is shut
 * out of it and no screen anywhere says so. That is why this warns even when nothing is "required".
 *
 * ⚠ It offers to CREATE the question (owner, 2026-09-25) rather than only pointing at the page: the
 * values that matter — `personField: email`, `requiredScope: adults` — are exactly the ones a host
 * filling the form by hand would leave wrong, and a wrong one here is indistinguishable from a right
 * one until somebody needs to sign.
 */
const AdultEmailGapAlert: React.FC<{ gap: ComplianceGap; eventId: string; onFixed: () => void; inWizard?: ComplianceGapWizardActions }> = ({
  gap,
  eventId,
  onFixed,
  inWizard,
}) => {
  const { t } = useTranslation(['events', 'common']);
  const navigate = useNavigate();
  const [adding, setAdding] = useState(false);
  const { turning, turnBackOn } = useTurnBackOn(gap.turnedOffQuestionId, onFixed);
  const isOff = !!gap.turnedOffQuestionId;

  const addQuestion = async () => {
    setAdding(true);
    const { askedOn, params } = ADULT_EMAIL_QUESTION(eventId);
    const result = await RsvpQuestionService.create(askedOn, params);
    setAdding(false);

    if (result?.code !== OP_RESULT_CODES.OK) {
      // ⚠ Only on FAILURE. The alert disappearing is the success signal — there is no row to
      // highlight and a toast beside a vanishing warning reads as a second, contradictory message.
      notifications.show({
        color: 'red',
        message: refusalReason(result) || t('common:errors.generic', { defaultValue: 'Something went wrong' }),
      });

      return;
    }

    onFixed();
  };

  return (
    <Alert
      color="orange"
      variant="light"
      icon={<AppIcon iconName="AlertTriangle" size={18} />}
      title={t('events:dash.adultEmailTitle', { defaultValue: 'Adults can’t receive what they have to sign' })}
    >
      <Stack gap="xs" align="flex-start">
        <Text size="md">
          {isOff
            ? t('events:dash.adultEmailOffBody', {
                defaultValue:
                  'An agreement on this event has to be signed by each adult, and they are emailed their own link to do it. Your email question is turned off, so guests are never asked it and there is no address to send the link to — the sale still goes through and the agreement is never signed.',
              })
            : gap.existsButOptional
            ? t('events:dash.adultEmailOptionalBody', {
                defaultValue:
                  'Your form asks for an email, but it is optional — so an adult who leaves it blank is still sent nothing. Make it required for adults.',
              })
            : t('events:dash.adultEmailBody', {
                defaultValue:
                  'An agreement on this event has to be signed by each adult, and they are emailed their own link to do it. Your form has no email question, so there is no address to send it to — the sale still goes through and the agreement is never signed, with nothing to show that it failed.',
              })}
        </Text>
        <Group gap="xs">
          {isOff ? (
            <Button size="md" loading={turning} onClick={() => void turnBackOn()}>
              {t('events:dash.adultEmailTurnOn', { defaultValue: 'Turn the email question back on' })}
            </Button>
          ) : (
            <Button size="md" loading={adding} onClick={addQuestion}>
              {t('events:dash.adultEmailAdd', { defaultValue: 'Add the email question' })}
            </Button>
          )}
          <Button
            size="md"
            variant="subtle"
            disabled={adding || turning}
            onClick={() => (inWizard ? inWizard.onReviewQuestions() : navigate(`/events/${eventId}/registration/questions`))}
          >
            {t('events:dash.adultEmailReview', { defaultValue: 'Review questions' })}
          </Button>
        </Group>
      </Stack>
    </Alert>
  );
};
